import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';

/**
 * Settings for the Platform Console operator's OWN account.
 *
 * Same shape as the chambers Settings shell (`features/settings/SettingsShell.tsx`) — back
 * link, eyebrow, title, one-line context, a section rail on the left, the section itself on the
 * right — and deliberately NOT the same implementation, because the two answer "which sections
 * exist?" from different places:
 *
 *  · Chambers Settings asks the server. Which sections a barrister, clerk or administrator is
 *    offered is a permissions answer, resolved against a registry, and hard-coding it in React
 *    would be a permissions lie.
 *  · The Platform Console has no such registry and NO ROLE TIERS — `PlatformAdmin` carries no
 *    role column, so every authenticated operator sees exactly these two sections (the trust
 *    model stated in `platform-team.routes.ts`). A fetched manifest here would be ceremony
 *    around a constant.
 *
 * What this is NOT is user management. Everything under it acts on the signed-in operator's own
 * account. Administering OTHER operators lives in Users and Support Executives, and the API
 * enforces that separately — the self-profile route carries no id to point elsewhere.
 */

/**
 * The Personal band, and only what the console can actually do.
 *
 * Notifications is ABSENT on purpose. The chambers Settings shell offers it because a `User` has
 * notification preferences to set; a `PlatformAdmin` has none — no preference storage, and no
 * optional mail (the invitation and a password reset are the only messages sent to an operator,
 * both transactional). A fourth entry here leading to an empty page would promise a capability
 * the product does not have, which is worse than its absence.
 */
const SECTIONS = [
  { to: '/platform/settings/account', label: 'My Account' },
  { to: '/platform/settings/security', label: 'Security' },
  { to: '/platform/settings/appearance', label: 'Appearance & Regional' },
] as const;

export function PlatformSettingsShell() {
  const navigate = useNavigate();

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-xs font-medium text-plat-muted transition-colors hover:text-plat-fg"
      >
        <CaretLeft className="h-3.5 w-3.5" />
        Back
      </button>

      <div className="mb-6 mt-3">
        <div className="text-xs font-semibold uppercase tracking-[0.08em] text-plat-muted">Settings</div>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-plat-fg">Settings</h1>
        <p className="mt-0.5 text-sm text-plat-muted">
          Your own Platform Console account. Tenants, operators and their access are managed
          elsewhere in the console.
        </p>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
        {/* A column on wide screens, a scrolling strip on narrow ones — the strip scrolls WITHIN
            itself, so the page never scrolls sideways. */}
        <nav aria-label="Settings sections" className="min-w-0 shrink-0 lg:w-60">
          <div className="flex gap-1 overflow-x-auto overflow-y-hidden pb-2 lg:flex-col lg:gap-0 lg:overflow-visible lg:overflow-y-visible lg:pb-0">
            <div className="hidden lg:mb-2 lg:block lg:px-3 lg:text-[11px] lg:font-semibold lg:uppercase lg:tracking-[0.08em] lg:text-plat-muted">
              Personal
            </div>
            {SECTIONS.map((s) => (
              <NavLink
                key={s.to}
                to={s.to}
                className={({ isActive }) =>
                  [
                    'flex shrink-0 items-center justify-between gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm transition',
                    'lg:w-full lg:whitespace-normal',
                    isActive
                      ? 'bg-plat-selected font-semibold text-plat-fg'
                      : 'font-medium text-plat-muted hover:bg-plat-hover hover:text-plat-fg',
                  ].join(' ')
                }
              >
                <span>{s.label}</span>
                <CaretRight className="hidden h-3.5 w-3.5 shrink-0 text-plat-faint lg:block" />
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="min-w-0 flex-1 pb-16">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
